import type { GridColDef, GridColumns } from '@mui/x-data-grid';
import transactions from 'config/tables/transactions.json';
import transactionList from 'config/tables/transactionList.json';
import merchants from 'config/tables/merchants.json';
import users from 'config/tables/users.json';
import merchantPayoutSummaryFiles from 'config/tables/merchantPayouts/summaryFiles.json';
import merchantPayoutFiles from 'config/tables/merchantPayouts/files.json';
import merchantPayoutCalculations from 'config/tables/merchantPayouts/calculations.json';
import merchantPayoutFileTaxCalculations from 'config/tables/merchantPayouts/fileTaxCalculations.json';
import transactionsMerchantPayoutIncrementsPivot from 'config/tables/transactionsMerchantPayoutIncrementsPivot.json';
import merchantPayoutIncrementTaxes from 'config/tables/merchantPayouts/incrementTaxes.json';
import merchantPayoutIncrements from 'config/tables/merchantPayouts/increments.json';
import merchantPayoutIncrementExchangeRates from 'config/tables/merchantPayouts/incrementExchangeRates.json';
import merchantPayoutSettlementsImportHistory from 'config/tables/merchantPayouts/settlementsImportHistory.json';
import allConsumers from 'config/tables/consumers/all.json';
import blacklistedConsumers from 'config/tables/consumers/blacklisted.json';
import whitelistedConsumers from 'config/tables/consumers/whiteListed.json';
import activeConsumers from 'config/tables/consumers/active.json';
import bankingSDK from 'config/tables/openBanking/bankingSDK.json';
import ibanXS from 'config/tables/openBanking/ibanXS.json';
import bankingSDKRecipients from 'config/tables/openBanking/bankingSDKRecipients.json';
import bankingSDKConnectorsRecipientsPivot from 'config/tables/openBanking/bankingBankingSDKConnectorsRecipientsPivot.json';
import ibanXSBanksRecipientsPivot from 'config/tables/openBanking/ibanXSBanksRecipientsPivot.json';
import ibanXSBanksCountriesPivot from 'config/tables/openBanking/ibanXSBanksCountriesPivot.json';
import manualBulkKYCsImportHistory from 'config/tables/manualBulkKYCsImportHistory.json';
import merchantUsers from 'config/tables/merchantUsers.json';
import ledgers from 'config/tables/ledgers.json';
import generalSetups from 'config/tables/setups/general.json';
import onboardingApplications from 'config/tables/onboarding/applications.json';
import waMessages from 'config/tables/waMessaging/waMessages.json';
import waMessagesSetups from 'config/tables/waMessaging/waMessagesSetups.json';
import phoneVerifications from 'config/tables/waMessaging/phoneVerifications.json';
import failedPhoneVerifications from 'config/tables/waMessaging/failedPhoneVerifications.json';
import waTemplatesSetups from 'config/tables/waMessaging/waTemplatesSetups.json';
import shipmentTracking from 'config/tables/shipmentTracking.json';

import { TableGridMode } from 'consts/table';
import { generalTableParser } from 'util/parsers';
import InvalidTableGridModeException from '../exceptions/InvalidTableGridModeException';
import { convertArrayToMap } from 'util/transformers';

const configRegistry = new Map<TableGridMode, GridColDef[]>([
    [TableGridMode.Transaction, typed(transactions)],
    [TableGridMode.Merchant, typed(merchants)],
    [TableGridMode.User, typed(users)],
    [TableGridMode.MerchantPayoutsSummaryFiles, typed(merchantPayoutSummaryFiles)],
    [TableGridMode.MerchantPayoutsFiles, typed(merchantPayoutFiles)],
    [TableGridMode.MerchantPayoutCalculations, typed(merchantPayoutCalculations)],
    [TableGridMode.MerchantPayoutFileTaxCalculations, typed(merchantPayoutFileTaxCalculations)],
    [TableGridMode.TransactionsMerchantPayoutIncrementsPivot, typed(transactionsMerchantPayoutIncrementsPivot)],
    [TableGridMode.MerchantPayoutIncrementTaxes, typed(merchantPayoutIncrementTaxes)],
    [TableGridMode.MerchantPayoutIncrements, typed(merchantPayoutIncrements)],
    [TableGridMode.MerchantPayoutIncrementExchangeRates, typed(merchantPayoutIncrementExchangeRates)],
    [TableGridMode.MerchantPayoutsSettlementsImportHistory, typed(merchantPayoutSettlementsImportHistory)],
    [TableGridMode.Consumer, typed(allConsumers)],
    [TableGridMode.BlacklistedConsumer, typed(blacklistedConsumers)],
    [TableGridMode.WhitelistedConsumer, typed(whitelistedConsumers)],
    [TableGridMode.ActiveConsumer, typed(activeConsumers)],
    [TableGridMode.ManualBulkKYCsImportHistory, typed(manualBulkKYCsImportHistory)],
    [TableGridMode.TransactionList, typed(transactionList)],
    [TableGridMode.MerchantUser, typed(merchantUsers)],
    [TableGridMode.MerchantLedger, typed(ledgers)],
    [TableGridMode.OpenBankingBankingSDK, typed(bankingSDK)],
    [TableGridMode.OpenBankingIbanXS, typed(ibanXS)],
    [TableGridMode.OpenBankingBankingSDKRecipients, typed(bankingSDKRecipients)],
    [TableGridMode.OpenBankingBankingSDKConnectorsRecipientsPivot, typed(bankingSDKConnectorsRecipientsPivot)],
    [TableGridMode.OpenBankingIbanXSBanksRecipientsPivot, typed(ibanXSBanksRecipientsPivot)],
    [TableGridMode.OpenBankingIbanXSBanksCountriesPivot, typed(ibanXSBanksCountriesPivot)],
    [TableGridMode.GeneralSetup, typed(generalSetups)],
    [TableGridMode.OnboardingApplications, typed(onboardingApplications)],
    [TableGridMode.WaMessages, typed(waMessages)],
    [TableGridMode.WaMessagesSetups, typed(waMessagesSetups)],
    [TableGridMode.PhoneVerifications, typed(phoneVerifications)],
    [TableGridMode.FailedPhoneVerifications, typed(failedPhoneVerifications)],
    [TableGridMode.WaTemplatesSetups, typed(waTemplatesSetups)],
    [TableGridMode.ShipmentTracking, typed(shipmentTracking)]
]);

export default function useTableConfig(mode: TableGridMode, overrideColumns: GridColumns) {
    if (!configRegistry.has(mode)) {
        throw new InvalidTableGridModeException(
            `Unknown table grid mode`,
            {}
        );
    }

    const columns = configRegistry.get(mode)!;

    const getGridColumnsWithOverrides = (columns: GridColumns) => {
        const indexColumn = 'field';

        const configColumnsMap = convertArrayToMap(columns, indexColumn);
        const overrideColumnsMap = convertArrayToMap(overrideColumns, indexColumn);

        // Override existing config
        columns.forEach(currentColumn => {
            const { field } = currentColumn;

            if (overrideColumnsMap.has(field)) {
                configColumnsMap.set(field, overrideColumnsMap.get(field));
                overrideColumnsMap.delete(field);
            }
        }, new Map<string, GridColDef>());

        // Add new columns
        return [
            ...Array.from(configColumnsMap.values()),
            ...Array.from(overrideColumnsMap.values())
        ];
    };

    const computedGridColumns = getGridColumnsWithOverrides(columns);

    return [
        generalTableParser(computedGridColumns),
        computedGridColumns
    ];
};


function typed<T = Array<GridColDef>>(columns: any) {
    return columns as T;
}
